import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  Activity,
  Search,
  RefreshCw,
  Clock,
  User,
  FileText,
  Gavel,
  Upload,
  MessageSquare,
  CheckSquare,
  Target,
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const eventIcons = {
  case_created: Target,
  report_imported: FileText,
  dispute_created: Gavel,
  dispute_sent: Gavel,
  dispute_resolved: Gavel,
  document_uploaded: Upload,
  document_requested: FileText,
  message_sent: MessageSquare,
  task_created: CheckSquare,
  task_completed: CheckSquare,
  status_changed: Activity,
};

const eventColors = {
  case_created: 'bg-emerald-100 text-emerald-600',
  report_imported: 'bg-emerald-100 text-emerald-600',
  dispute_created: 'bg-amber-100 text-amber-600',
  dispute_sent: 'bg-amber-100 text-amber-600',
  dispute_resolved: 'bg-emerald-100 text-emerald-600',
  document_uploaded: 'bg-amber-100 text-amber-600',
  document_requested: 'bg-amber-100 text-amber-600',
  message_sent: 'bg-emerald-100 text-emerald-600',
  task_created: 'bg-slate-100 text-slate-600',
  task_completed: 'bg-emerald-100 text-emerald-600',
  status_changed: 'bg-slate-100 text-slate-600',
};

const StaffActivityPage = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchActivity = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/staff/activity?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(response.data.events || []);
    } catch (err) {
      console.error('Failed to fetch activity:', err);
      setError(err.response?.data?.detail || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchActivity();
  }, [token]);

  const filteredEvents = events.filter(e => {
    if (!searchTerm) return true;
    const title = e.title?.toLowerCase() || '';
    const description = e.description?.toLowerCase() || '';
    return title.includes(searchTerm.toLowerCase()) || 
           description.includes(searchTerm.toLowerCase());
  });

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-activity-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Activity Feed</h1>
          <p className="text-slate-500 mt-1">Recent activity across the agency</p>
        </div>
        <Button onClick={fetchActivity} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search activity..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Activity Timeline */}
      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <div className="animate-pulse space-y-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-slate-100" />
                <div className="flex-1">
                  <div className="h-4 bg-slate-100 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : filteredEvents.length > 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="divide-y divide-slate-100">
            {filteredEvents.map((event, index) => {
              const EventIcon = eventIcons[event.event_type] || Activity;
              const colorClass = eventColors[event.event_type] || 'bg-slate-100 text-slate-600';
              return (
                <div
                  key={event.event_id || index}
                  className="p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`activity-event-${event.event_id || index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <EventIcon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-medium text-stone-900">{event.title || 'Activity'}</h3>
                          {event.description && (
                            <p className="text-sm text-slate-500 mt-1">{event.description}</p>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 flex-shrink-0 flex items-center gap-1">
                          <Clock size={12} />
                          {getTimeAgo(event.created_at)}
                        </span>
                      </div>
                      {event.actor_user_id && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                          <User size={12} />
                          <span>By user</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Activity size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No recent activity</h3>
          <p className="text-slate-500">
            {searchTerm
              ? 'Try adjusting your search'
              : 'Activity will appear here as cases are worked on'}
          </p>
        </div>
      )}
    </div>
  );
};

export default StaffActivityPage;
